// src/pages/DetailChecklistDrone.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { API_URL, API_URL_PDF } from "../api";
import dayjs from "dayjs";

export default function DetailChecklistDrone() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openReject, setOpenReject] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [notif, setNotif] = useState({ open: false, message: "" });

  // Check if equipment has valid status items
  const hasValidEquipment = (equipment) => {
    return equipment?.some((item) => item.status && item.status.trim() !== "");
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/drone-checklists/${id}`);
      let checklistData = res.data.data || res.data;

      const parseJSON = (field) => {
        if (typeof field === "string") {
          try {
            return JSON.parse(field);
          } catch (e) {
            console.error("Gagal parsing field:", field, e);
            return [];
          }
        }
        return Array.isArray(field) ? field : [];
      };

      // Parse all equipment fields
      checklistData.equipment_m3 = parseJSON(checklistData.equipment_m3);
      checklistData.equipment_m350 = parseJSON(checklistData.equipment_m350);
      checklistData.equipment_gnss = parseJSON(checklistData.equipment_gnss);
      checklistData.pre_flight_checks = parseJSON(
        checklistData.pre_flight_checks
      );

      if (typeof checklistData.foto_dokumentasi === "string") {
        try {
          const parsed = JSON.parse(checklistData.foto_dokumentasi);
          checklistData.foto_dokumentasi = Array.isArray(parsed)
            ? parsed
            : [parsed];
        } catch (e) {
          checklistData.foto_dokumentasi = [checklistData.foto_dokumentasi];
        }
      } else if (!Array.isArray(checklistData.foto_dokumentasi)) {
        checklistData.foto_dokumentasi = [];
      }

      setDetail(checklistData);
    } catch (err) {
      console.error("Gagal memuat detail:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      // Menggunakan PATCH sesuai referensi untuk update parsial
      await axios.patch(`${API_URL}/drone-checklists/${id}/status`, {
        status: "Disetujui",
        keterangan: "", // Mengirim keterangan kosong sesuai referensi
      });
      setNotif({ open: true, message: "Checklist berhasil disetujui!" });
      fetchDetail();
    } catch (err) {
      console.error("Gagal menyetujui:", err);
      setNotif({ open: true, message: "Gagal menyetujui checklist" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setActionLoading(true);
      // Menggunakan PATCH dan menyesuaikan nama field menjadi 'keterangan'
      await axios.patch(`${API_URL}/drone-checklists/${id}/status`, {
        status: "Ditolak",
        keterangan: rejectReason,
      });
      setOpenReject(false);
      setNotif({ open: true, message: "Checklist berhasil ditolak!" });
      fetchDetail();
    } catch (err) {
      console.error("Gagal menolak:", err);
      setNotif({ open: true, message: "Gagal menolak checklist" });
    } finally {
      setActionLoading(false);
    }
  };

  const ChecklistItemView = ({ item, index }) => (
    <Box sx={{ mb: 1.5, pl: 1, borderLeft: "2px solid #eee" }}>
      <Typography>
        {index + 1}. <b>{item.item_name || "Item tidak bernama"}</b>
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, pl: 2, mt: 1 }}>
        <FormControlLabel
          control={
            <Checkbox checked={item.status === "Ok"} disabled color="success" />
          }
          label="Ok"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={item.status === "Not Ok"}
              disabled
              color="error"
            />
          }
          label="Not Ok"
        />
        {item.jumlah && (
          <Typography variant="body2" color="text.secondary">
            Jumlah: {item.jumlah}
          </Typography>
        )}
      </Box>
      {item.keterangan && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ pl: 2, mt: 1 }}
        >
          Keterangan: {item.keterangan}
        </Typography>
      )}
    </Box>
  );

  const PreFlightItemView = ({ item, index }) => (
    <Box sx={{ mb: 1.5, pl: 1, borderLeft: "2px solid #eee" }}>
      <Typography>
        {index + 1}. <b>{item.item_name || "Item tidak bernama"}</b>
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, pl: 2, mt: 1 }}>
        <FormControlLabel
          control={
            <Checkbox checked={item.status === "Ok"} disabled color="success" />
          }
          label="Ok"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={item.status === "Not Ok"}
              disabled
              color="error"
            />
          }
          label="Not Ok"
        />
      </Box>
      {item.keterangan && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ pl: 2, mt: 1 }}
        >
          Keterangan: {item.keterangan}
        </Typography>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!detail) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>Data tidak ditemukan</Typography>
      </Container>
    );
  }

  return (
    <div className="form-container">
      <header className="app-header">
        <div className="header-content">
          <button
            className="back-button"
            onClick={() => navigate("/menu-drone-checklist")}
          >
            <Icon>arrow_back</Icon>
          </button>
          <h1 className="header-title">Detail Checklist Drone</h1>
          <div className="header-spacer"></div>
        </div>
      </header>
      <Container sx={{ mt: 3, mb: 5 }}>
        {/* Basic Information */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6">Informasi Dasar</Typography>
          <Divider sx={{ my: 1 }} />
          <Typography>
            <b>Lokasi:</b> {detail.lokasi}
          </Typography>
          <Typography>
            <b>Tanggal:</b>{" "}
            {dayjs(detail.created_at).format("DD MMMM YYYY, HH:mm")}
          </Typography>
          <Typography>
            <b>Pilot:</b> {detail.pilot}
          </Typography>
          <Typography>
            <b>Supervisor:</b> {detail.supervisor}
          </Typography>
          <Typography>
            <b>Status:</b> {detail.status}
          </Typography>
          {detail.status === "Ditolak" && (
            <Typography color="error">
              <b>Alasan Penolakan:</b> {detail.alasan_penolakan}
            </Typography>
          )}
        </Paper>

        {/* Equipment Section */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6">Checklist Peralatan</Typography>
          <Divider sx={{ my: 1 }} />

          {/* DJI M3 - Only show if has valid status items */}
          {hasValidEquipment(detail.equipment_m3) && (
            <>
              <Typography
                variant="subtitle1"
                sx={{ mt: 2, fontWeight: "bold" }}
              >
                DJI M3 ENTERPRISE
              </Typography>
              {detail.equipment_m3.map(
                (item, index) =>
                  item.status &&
                  item.status.trim() !== "" && (
                    <ChecklistItemView
                      key={`m3-${index}`}
                      item={item}
                      index={index}
                    />
                  )
              )}
            </>
          )}

          {/* DJI M350 - Only show if has valid status items */}
          {hasValidEquipment(detail.equipment_m350) && (
            <>
              <Typography
                variant="subtitle1"
                sx={{ mt: 2, fontWeight: "bold" }}
              >
                DJI M350 RTK
              </Typography>
              {detail.equipment_m350.map(
                (item, index) =>
                  item.status &&
                  item.status.trim() !== "" && (
                    <ChecklistItemView
                      key={`m350-${index}`}
                      item={item}
                      index={index}
                    />
                  )
              )}
            </>
          )}

          {/* GNSS - Always show if exists */}
          {detail.equipment_gnss?.length > 0 && (
            <>
              <Typography
                variant="subtitle1"
                sx={{ mt: 2, fontWeight: "bold" }}
              >
                GNSS EMLID RS3
              </Typography>
              {detail.equipment_gnss.map((item, index) => (
                <ChecklistItemView
                  key={`gnss-${index}`}
                  item={item}
                  index={index}
                />
              ))}
            </>
          )}

          {!hasValidEquipment(detail.equipment_m3) &&
            !hasValidEquipment(detail.equipment_m350) &&
            detail.equipment_gnss?.length === 0 && (
              <Typography sx={{ mt: 2, color: "text.secondary" }}>
                Tidak ada data peralatan yang valid
              </Typography>
            )}
        </Paper>

        {/* Pre-flight Check */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6">Pre-flight Check</Typography>
          <Divider sx={{ my: 1 }} />
          {detail.pre_flight_checks?.map((item, index) => (
            <PreFlightItemView
              key={`preflight-${index}`}
              item={item}
              index={index}
            />
          ))}
        </Paper>

        {/* Documentation Photos */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6">Foto Dokumentasi</Typography>
          {detail.foto_dokumentasi?.length > 0 ? (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {detail.foto_dokumentasi.map((foto, idx) => {
                const imageUrl = `${API_URL_PDF}/public${foto}`;
                return (
                  <Grid xs={6} md={3} key={idx}>
                    <img
                      src={imageUrl}
                      alt={`Foto ${idx + 1}`}
                      style={{
                        width: "100%",
                        height: 150,
                        objectFit: "cover",
                        borderRadius: 8,
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/placeholder.png";
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Typography sx={{ mt: 1, color: "text.secondary" }}>
              Tidak ada foto dokumentasi
            </Typography>
          )}
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2 }}>
          {detail.status === "Menunggu" && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={handleApprove}
                disabled={actionLoading}
                startIcon={
                  actionLoading ? <CircularProgress size={24} /> : null
                }
              >
                Setujui
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => setOpenReject(true)}
                disabled={actionLoading}
              >
                Tolak
              </Button>
            </>
          )}
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Kembali
          </Button>
        </Box>

        {/* Reject Dialog */}
        <Dialog open={openReject} onClose={() => setOpenReject(false)}>
          <DialogTitle>Alasan Penolakan</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Masukkan alasan penolakan"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenReject(false)}>Batal</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleReject}
              disabled={!rejectReason.trim() || actionLoading}
              startIcon={actionLoading ? <CircularProgress size={24} /> : null}
            >
              Kirim
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Dialog */}
        <Dialog
          open={notif.open}
          onClose={() => setNotif({ open: false, message: "" })}
        >
          <DialogTitle>Informasi</DialogTitle>
          <DialogContent>
            <Typography>{notif.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNotif({ open: false, message: "" })}>
              Tutup
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
}
